import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/api/client';
import AddressFields from './AddressFields';
import { FieldError } from './form-utils';
import { errorClass, useFieldErrors } from './use-field-errors';

export default function ReturnAddressForm({ address, onClose }) {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();

  const [formData, setFormData] = useState({
    name: address?.name ?? '',
    contactName: address?.contactName ?? '',
    phone: address?.phone ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    country: address?.country ?? 'US',
    isDefault: address?.isDefault ?? false,
  });

  const saveMutation = useMutation({
    mutationFn: (data) =>
      address
        ? api.entities.ReturnAddress.update(address.id, data)
        : api.entities.ReturnAddress.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['returnAddresses'] });
      toast.success(address ? 'Return address updated' : 'Return address added');
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to save return address'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    saveMutation.mutate(formData);
  };

  return (
    <Card className="border-red-200 shadow-md">
      <CardHeader className="bg-red-50 border-b">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">
            {address ? 'Edit return address' : 'Add return address'}
          </CardTitle>
          <Button type="button" variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ra-name">Label</Label>
            <Input
              id="ra-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Main Office, Warehouse"
              className={errorClass(fieldErrors.name)}
              required
            />
            <FieldError message={fieldErrors.name} />
            <p className="text-xs text-slate-500">How this address appears in the Mail page list.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra-contact">Contact name (optional)</Label>
            <Input
              id="ra-contact"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Shipping Department"
              className={errorClass(fieldErrors.contactName)}
            />
            <FieldError message={fieldErrors.contactName} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra-phone">Phone number</Label>
            <Input
              id="ra-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="555-555-0100"
              className={errorClass(fieldErrors.phone)}
              required
            />
            <FieldError message={fieldErrors.phone} />
            <p className="text-xs text-slate-500">Required — USPS will not quote rates without it.</p>
          </div>

          <AddressFields
            formData={formData}
            setFormData={setFormData}
            fieldErrors={fieldErrors}
            idPrefix="ra"
          />

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="ra-default" className="cursor-pointer">
                Use as default
              </Label>
              <p className="text-xs text-slate-500">Pre-selected when creating a single label.</p>
            </div>
            <Switch
              id="ra-default"
              checked={formData.isDefault}
              onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save address'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
